/**
 * Destructive-Op Action Definitions
 *
 * From `gibbs-destructive-ops-ux` (Phase 3, 2026-05-03). The two
 * UserActions in this file `destructiveConfirm: true`, which makes
 * the chain interceptor in `text-to-action/index.js` emit a confirm
 * clarify before dispatch.
 *
 * Pattern reuse: any future destructive ActionDef can opt in by
 * setting `destructiveConfirm: true` and a ≥ 40-char `confirmMessage`.
 *
 * @module gibbs/registry/actions/destructive-ops
 */

import { param } from '../schema';

// ─── Zap a connected-data session (the more aggressive one) ────────────────

export const zapConnection = {
    id: 'zap-connection',
    name: 'Zap Connected-Data Session',
    category: 'External Data Retrieval',
    description: 'Drop the active connected-data session and clear all cached protein maps, in-flight queries, and connection state. Use only when the user explicitly wants to start over with a clean session.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ZapConnection',
    serverCommand: null,
    jobType: null,
    destructiveConfirm: true,
    confirmMessage: 'This will drop the active connected-data session, '
        + 'clearing all cached protein maps, in-flight queries, and the '
        + 'connection itself. Any unsaved analysis is lost. Confirm to '
        + 'proceed; cancel to keep your current session intact.',
};

// ─── Remove one specific connection ────────────────────────────────────────

export const removeConnection = {
    id: 'remove-connection',
    name: 'Remove Data Connection',
    category: 'External Data Retrieval',
    description: 'Remove a specific data connection from the workspace. This drops cached fragment maps and in-flight molecule fetches associated with that connection only — other connections remain. If this is the primary (or last) connection, behavior matches `zap-connection` because removing the only connection requires zapping the session.',
    inputs: [
        param('connector', 'object', 'The connection (connector handle) to remove', true),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'RemoveConnection',
    serverCommand: null,
    jobType: null,
    destructiveConfirm: true,
    confirmMessage: 'This will drop the named data connection plus its '
        + 'cached protein maps and in-flight molecule fetches. Other '
        + 'data connections remain. If this is the last connection, '
        + 'the entire session zaps. Confirm to proceed.',
};

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    zapConnection,
    removeConnection,
];
